import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { parsePresetDuration, presetDurationInput, formatPresetDuration } from "../../utils/presetDuration";
import { limits } from "../../Constants/constants";

const CreatePreset = (props) => {
  const [presetName, setPresetName] = useState("");
  const [isDailyRoutine, setIsDailyRoutine] = useState(false);
  const [taskName, setTaskName] = useState("");
  const [taskTime, setTaskTime] = useState("");
  const [tasksList, setTasksList] = useState([]);
  const [step, setStep] = useState(1);
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [editTaskName, setEditTaskName] = useState("");
  const [editTaskTime, setEditTaskTime] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const isListInput = (() => {
    const value = taskName.trim();
    return value.startsWith("[") && value.endsWith("]");
  })();

  const totalMinutes = tasksList.reduce(
    (total, task) => total + Number(task.estimatedTaskTime || 0),
    0
  );

  const parseTaskListInput = (rawInput) => {
    const trimmed = rawInput.trim().replace(/^\[|\]$/g, "").trim();
    if (!trimmed) return { ok: false, error: "The task list is empty." };

    const items = trimmed.split(/\s*,\s*/);
    const parsed = [];

    for (const item of items) {
      const match = item.match(/^(.*?):\s*(\d+(?:\.\d{1,2})?)$/);
      if (!match) {
        return {
          ok: false,
          error: "Use this format: [Task one:10, Task two:15]",
        };
      }

      let name = match[1].trim();
      if (
        (name.startsWith("'") && name.endsWith("'")) ||
        (name.startsWith('"') && name.endsWith('"'))
      ) {
        name = name.slice(1, -1).trim();
      }

      const minutes = parsePresetDuration(match[2], limits.maxTimeLimitPreset);
      if (!name || !Number.isFinite(minutes) || minutes > limits.maxTimeLimitPreset) {
        return {
          ok: false,
          error: `Each task needs a name and 1 second to ${limits.maxTimeLimitPreset} minutes.`,
        };
      }
      parsed.push({ taskName: name, estimatedTaskTime: minutes });
    }

    return { ok: true, tasks: parsed };
  };

  const addTasksWithinLimit = (newTasks) => {
    const remainingSlots = limits.maxTasksPerPreset - tasksList.length;
    if (remainingSlots <= 0) {
      alert(`A preset can contain up to ${limits.maxTasksPerPreset} tasks.`);
      return;
    }

    setTasksList([...tasksList, ...newTasks.slice(0, remainingSlots)]);
    if (newTasks.length > remainingSlots) {
      alert(`Only the first ${remainingSlots} tasks were added.`);
    }
  };

  const handleAddTask = () => {
    if (isListInput) {
      const result = parseTaskListInput(taskName);
      if (!result.ok) {
        alert(result.error);
        return;
      }
      addTasksWithinLimit(result.tasks);
    } else {
      const minutes = parsePresetDuration(taskTime, limits.maxTimeLimitPreset);
      if (
        !taskName.trim() ||
        !minutes ||
        !Number.isFinite(minutes) ||
        minutes > limits.maxTimeLimitPreset
      ) {
        alert(
          `Enter a task name and a duration from 1 second to ${limits.maxTimeLimitPreset} minutes (0.05 = 5 seconds).`
        );
        return;
      }
      addTasksWithinLimit([
        { taskName: taskName.trim(), estimatedTaskTime: minutes },
      ]);
    }

    setTaskName("");
    setTaskTime("");
  };

  const handleCollectFromMainList = () => {
    const mainTasks = props.mainTasks || [];
    if (mainTasks.length === 0) {
      alert("There are no tasks in the main list to collect.");
      return;
    }

    addTasksWithinLimit(
      mainTasks.map((task) => ({
        taskName: task.taskName,
        estimatedTaskTime: Math.max(1, Math.round(task.estimatedTaskTime || 0)) / 60,
      }))
    );
  };

  const goToNextStep = () => {
    if (!presetName.trim()) {
      alert("Give your preset a name first.");
      return;
    }
    setStep(2);
  };

  const handleSavePreset = () => {
    if (tasksList.length < 2) {
      alert("Add at least 2 tasks before saving this preset.");
      return;
    }
    props.addPreset(presetName.trim(), tasksList, isDailyRoutine);
  };

  const handleEditTaskClick = (index) => {
    setEditingTaskIndex(index);
    setEditTaskName(tasksList[index].taskName);
    setEditTaskTime(presetDurationInput(tasksList[index].estimatedTaskTime));
    setOpenEditDialog(true);
  };

  const closeEditDialog = () => {
    setOpenEditDialog(false);
    setEditingTaskIndex(null);
    setEditTaskName("");
    setEditTaskTime("");
  };

  const handleSaveEditTask = () => {
    const minutes = parsePresetDuration(editTaskTime, limits.maxTimeLimitPreset);
    if (
      !editTaskName.trim() ||
      !minutes ||
      !Number.isFinite(minutes) ||
      minutes > limits.maxTimeLimitPreset
    ) {
      alert("Enter valid task details.");
      return;
    }

    const updatedTasks = [...tasksList];
    updatedTasks[editingTaskIndex] = {
      taskName: editTaskName.trim(),
      estimatedTaskTime: minutes,
    };
    setTasksList(updatedTasks);
    closeEditDialog();
  };

  const handleDrop = (dropIndex) => {
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const reorderedTasks = [...tasksList];
    const [draggedTask] = reorderedTasks.splice(draggedIndex, 1);
    reorderedTasks.splice(dropIndex, 0, draggedTask);
    setTasksList(reorderedTasks);
    setDraggedIndex(null);
  };

  return (
    <section className="create-preset">
      <header className="create-preset-header">
        <button
          type="button"
          className="edit-preset-back"
          onClick={step === 1 ? props.cancelCreate : () => setStep(1)}
          aria-label={step === 1 ? "Back to my presets" : "Back to preset name"}
        >
          <ArrowBackRoundedIcon />
        </button>
        <div>
          <h2 title={step === 2 ? presetName : undefined}>
            {step === 1 ? "New preset" : presetName}
          </h2>
          <p>
            {step === 1
              ? "Start with a memorable name."
              : "Tasks added below will be saved in this preset."}
          </p>
        </div>
        <span className="create-preset-step-label">Step {step} of 2</span>
      </header>

      <div className="create-preset-progress" aria-hidden="true">
        <span className="is-complete" />
        <span className={step === 2 ? "is-complete" : ""} />
      </div>

      {step === 1 ? (
        <div className="create-preset-name-step">
          <h3>Choose a preset name</h3>
          <p>
            Use a short, clear name so you can quickly recognize this routine
            later.
          </p>

          <label className="edit-preset-label" htmlFor="new-preset-name">
            Preset name
          </label>
          <input
            id="new-preset-name"
            className="edit-preset-input create-preset-name-input"
            value={presetName}
            onChange={(event) => setPresetName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") goToNextStep();
            }}
            maxLength={limits.maxBucketNameLimit}
            placeholder="e.g. Morning focus"
            autoFocus
          />
          <div className="create-preset-name-meta">
            <span>Examples: Morning routine, Study sprint, Quick reset</span>
            <span>
              {presetName.length}/{limits.maxBucketNameLimit}
            </span>
          </div>

          <label className="preset-checkbox">
            <input
              type="checkbox"
              checked={isDailyRoutine}
              onChange={(event) => setIsDailyRoutine(event.target.checked)}
            />
            <span>Daily routine</span>
          </label>

          <footer className="create-preset-name-actions">
            <button
              type="button"
              className="edit-preset-cancel"
              onClick={props.cancelCreate}
            >
              Cancel
            </button>
            <button
              type="button"
              className="edit-preset-save"
              onClick={goToNextStep}
              disabled={!presetName.trim()}
            >
              <span>Continue</span>
              <ArrowForwardRoundedIcon />
            </button>
          </footer>
        </div>
      ) : (
        <div className="create-preset-build-step">
          <div className="edit-preset-tasks create-preset-tasks">
            <div className="edit-preset-tasks-header">
              <div>
                <h3>Tasks in this preset</h3>
                <p>Review, edit, or drag tasks to reorder them.</p>
              </div>
              <div className="edit-preset-list-tools">
                <span>
                  {tasksList.length} tasks · {formatPresetDuration(totalMinutes)}
                </span>
                <button
                  type="button"
                  className="edit-preset-collect"
                  onClick={handleCollectFromMainList}
                  title="Import tasks from the main list"
                >
                  <PlaylistAddRoundedIcon />
                  <span>Import Current Tasks</span>
                </button>
              </div>
            </div>

            <div className="edit-preset-task-list">
              {tasksList.length === 0 ? (
                <div className="create-preset-empty-tasks">
                  <PlaylistAddRoundedIcon />
                  <span>Your preset is ready for its first task.</span>
                </div>
              ) : (
                tasksList.map((task, index) => (
                  <div
                    key={`${task.taskName}-${index}`}
                    className={`edit-preset-task-row ${
                      draggedIndex === index ? "is-dragging" : ""
                    }`}
                    draggable
                    onDragStart={() => setDraggedIndex(index)}
                    onDragEnd={() => setDraggedIndex(null)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleDrop(index)}
                  >
                    <DragIndicatorRoundedIcon className="edit-preset-drag-icon" />
                    <span className="edit-preset-task-number">{index + 1}</span>
                    <span className="edit-preset-task-name">
                      {task.taskName}
                    </span>
                    <span className="edit-preset-task-duration">
                      {formatPresetDuration(task.estimatedTaskTime)}
                    </span>
                    <button
                      type="button"
                      className="edit-preset-task-action"
                      onClick={() => handleEditTaskClick(index)}
                      aria-label={`Edit ${task.taskName}`}
                    >
                      <EditRoundedIcon />
                    </button>
                    <button
                      type="button"
                      className="edit-preset-task-action edit-preset-task-delete"
                      onClick={() =>
                        setTasksList(
                          tasksList.filter(
                            (_, taskIndex) => taskIndex !== index
                          )
                        )
                      }
                      aria-label={`Delete ${task.taskName}`}
                    >
                      <DeleteOutlineRoundedIcon />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="edit-preset-section edit-preset-composer-section">
            <div className="edit-preset-section-heading">
              <div>
                <h3>Add a task</h3>
                <p>Time: 0.1 = 10 sec, 0.05 = 5 sec, 1.05 = 1 min 5 sec.</p>
                <p>Add one task or paste a formatted task list.</p>
              </div>
            </div>

            <div
              className={`edit-preset-composer ${
                isListInput ? "create-preset-bulk-composer" : ""
              }`}
            >
              <input
                className="edit-preset-input edit-preset-task-input"
                value={taskName}
                onChange={(event) => {
                  setTaskName(
                    event.target.value.slice(0, limits.maxInputCharacters)
                  );
                  if (
                    event.target.value.trim().startsWith("[") &&
                    event.target.value.trim().endsWith("]")
                  ) {
                    setTaskTime("");
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleAddTask();
                }}
                placeholder={
                  isListInput ? "[Task:10, Task:15]" : "Task name"
                }
              />
              {!isListInput && (
                <div className="edit-preset-time-field">
                  <AccessTimeRoundedIcon />
                  <input
                    value={taskTime}
                    onChange={(event) => setTaskTime(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleAddTask();
                    }}
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={limits.maxTimeLimitPreset}
                    placeholder="Min.sec"
                    aria-label="Task duration in minutes and seconds"
                  />
                </div>
              )}
              <button
                type="button"
                className="edit-preset-add"
                onClick={handleAddTask}
              >
                <AddRoundedIcon />
                <span>Add</span>
              </button>
            </div>
            <p className="create-preset-bulk-hint">
              Bulk add example: <code>[Read:10, Exercise:15]</code>
            </p>
          </div>

          <footer className="edit-preset-actions create-preset-actions">
            <button
              type="button"
              className="edit-preset-cancel"
              onClick={() => setStep(1)}
            >
              Back
            </button>
            <span className="create-preset-save-note">
              {tasksList.length < 2
                ? `${2 - tasksList.length} more task${
                    2 - tasksList.length === 1 ? "" : "s"
                  } needed`
                : "Ready to save"}
            </span>
            <button
              type="button"
              className="edit-preset-save"
              onClick={handleSavePreset}
              disabled={tasksList.length < 2}
            >
              <SaveRoundedIcon />
              <span>Save preset</span>
            </button>
          </footer>
        </div>
      )}

      <Dialog
        open={openEditDialog}
        onClose={closeEditDialog}
        className="edit-task-dialog"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Edit task</DialogTitle>
        <DialogContent>
          <p className="edit-task-dialog-copy">
            Update this task before saving your preset.
          </p>
          <div className="edit-task-dialog-fields">
            <TextField
              fullWidth
              size="small"
              label="Task name"
              value={editTaskName}
              onChange={(event) => setEditTaskName(event.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Duration (min.sec)"
              value={editTaskTime}
              onChange={(event) => setEditTaskTime(event.target.value)}
              type="number"
              helperText="0.1 = 10 sec, 0.05 = 5 sec, 1.05 = 1 min 5 sec"
              inputProps={{ min: 0.01, step: 0.01, max: limits.maxTimeLimitPreset }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="edit-preset-cancel"
            onClick={closeEditDialog}
          >
            Cancel
          </button>
          <button
            type="button"
            className="edit-preset-save"
            onClick={handleSaveEditTask}
          >
            Save task
          </button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default CreatePreset;
